import React, { useState } from 'react';
import { VibeScoreService, VibeScoreResult, calculateVibeScore } from '../services/vibeScoreService';
import ChakraBar from './ChakraBar';

interface VibeScoreCardProps {
  initialContent?: string;
  onScoreUpdate?: (result: VibeScoreResult) => void;
}

const VibeScoreCard: React.FC<VibeScoreCardProps> = ({ initialContent = '', onScoreUpdate }) => {
  const [content, setContent] = useState(initialContent);
  const [result, setResult] = useState<VibeScoreResult | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const analyzeContent = async () => {
    if (!content.trim()) return;
    
    setIsAnalyzing(true);
    
    // Simulate API delay for better UX
    setTimeout(() => {
      const analysisResult = calculateVibeScore(content);
      setResult(analysisResult);
      onScoreUpdate?.(analysisResult);
      setIsAnalyzing(false);
    }, 800);
  };

  const getScoreEmoji = (score: number) => {
    if (score >= 80) return '🌟';
    if (score >= 60) return '✨';
    if (score >= 40) return '💫';
    if (score >= 20) return '⚡';
    return '🔴';
  };

  const getCategoryIcon = (score: number) => {
    if (score >= 75) return '🟢';
    if (score >= 45) return '🟡';
    return '🔴';
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
          <span className="text-2xl">🎯</span>
        </div>
        <div>
          <h3 className="text-xl font-bold text-gray-900">VibeScore Analyzer</h3>
          <p className="text-gray-600">Analyseer de energetische frequentie van je content</p>
        </div>
      </div>

      {/* Content Input */}
      <div className="mb-6">
        <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-2">
          Plak je post content hier:
        </label>
        <textarea
          id="content"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Type of plak hier je post content om de VibeScore te analyseren..."
          className="w-full h-32 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
        />
        <div className="mt-2 flex justify-between items-center">
          <span className="text-sm text-gray-500">
            {content.length} karakters
          </span>
          <button
            onClick={analyzeContent}
            disabled={!content.trim() || isAnalyzing}
            className="px-6 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {isAnalyzing ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Analyseren...</span>
              </>
            ) : (
              <>
                <span>🎯</span>
                <span>Analyseer VibeScore</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Results */}
      {result && (
        <div className="space-y-6">
          {/* Main Score Display */}
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <span className="text-3xl">{getScoreEmoji(result.score)}</span>
                <div>
                  <h4 className="text-2xl font-bold text-gray-900">VibeScore: {result.score}/100</h4>
                  <p className="text-gray-600">{result.chakraColor.description}</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-500">Categorie</div>
                <div className="flex items-center space-x-2">
                  <span className="text-xl">{getCategoryIcon(result.score)}</span>
                  <span className="font-semibold capitalize">{result.chakraColor.name}</span>
                </div>
              </div>
            </div>

            {/* Chakra Meter */}
            <div className="mb-4">
              <ChakraBar 
                score={result.score} 
                breakdown={result.breakdown}
                showDetails={false}
              />
            </div>
          </div>

          {/* Detailed Analysis */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-green-50 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <span className="text-green-600">📈</span>
                <h5 className="font-semibold text-green-800">Positieve Woorden</h5>
              </div>
              <p className="text-2xl font-bold text-green-600">{result.breakdown.wordMatches.positive.length}</p>
              <p className="text-sm text-green-700">Woorden met hoge frequentie</p>
            </div>

            <div className="bg-red-50 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <span className="text-red-600">📉</span>
                <h5 className="font-semibold text-red-800">Negatieve Woorden</h5>
              </div>
              <p className="text-2xl font-bold text-red-600">{result.breakdown.wordMatches.negative.length}</p>
              <p className="text-sm text-red-700">Woorden met lage frequentie</p>
            </div>

            <div className="bg-yellow-50 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <span className="text-yellow-600">🤔</span>
                <h5 className="font-semibold text-yellow-800">Sarcasme</h5>
              </div>
              <p className="text-lg font-bold text-yellow-600">
                {result.breakdown.isSarcastic ? 'Gedetecteerd' : 'Niet gedetecteerd'}
              </p>
              <p className="text-sm text-yellow-700">
                {result.breakdown.isSarcastic ? 'Kan score beïnvloeden' : 'Geen sarcasme gevonden'}
              </p>
            </div>

            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <span className="text-blue-600">🌍</span>
                <h5 className="font-semibold text-blue-800">Culturele Aanpassing</h5>
              </div>
              <p className="text-lg font-bold text-blue-600">
                +{((result.breakdown.culturalMultiplier - 1) * 100).toFixed(1)}%
              </p>
              <p className="text-sm text-blue-700">Culturele aanpassing ({result.language})</p>
            </div>
          </div>

          {/* Tips */}
          <div className="bg-purple-50 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <span className="text-purple-600">💡</span>
              <h5 className="font-semibold text-purple-800">Tips voor verbetering</h5>
            </div>
            <div className="text-sm text-purple-700 space-y-1">
              {result.score < 50 && (
                <p>• Gebruik meer positieve, verbindende woorden zoals "samen", "groei", "succes"</p>
              )}
              {result.breakdown.wordMatches.negative.length > result.breakdown.wordMatches.positive.length && (
                <p>• Probeer negatieve woorden te vervangen door constructieve alternatieven</p>
              )}
              {result.breakdown.isSarcastic && (
                <p>• Overweeg om sarcasme te vervangen door directe, eerlijke communicatie</p>
              )}
              {result.breakdown.bonuses.hasQuestion && (
                <p>• ✅ Goed: Je stelt vragen die community engagement stimuleren</p>
              )}
              {result.breakdown.bonuses.hasSpecificNumbers && (
                <p>• ✅ Goed: Concrete data maakt je content waardevoller</p>
              )}
              {result.breakdown.bonuses.isEducational && (
                <p>• ✅ Goed: Educatieve content krijgt een bonus voor waarde</p>
              )}
              <p>• Focus op authentieke, waardevolle content die anderen inspireert</p>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!result && !isAnalyzing && (
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">🎯</span>
          </div>
          <h4 className="text-lg font-semibold text-gray-900 mb-2">Klaar voor analyse</h4>
          <p className="text-gray-600">
            Plak je post content hierboven en klik op "Analyseer VibeScore" om te beginnen
          </p>
        </div>
      )}
    </div>
  );
};

export default VibeScoreCard;
